import { useAuth } from "@/auth/Auth";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import LayoutOutside from "@/layout/LayoutOutside";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFetchInvite } from "./hooks/useFetchInvite";
import { useApplyInviteAffiliation } from "./hooks/useApplyInviteAffiliation";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useEffect } from "react";
import Loader from "@/components/Loader/Loader";

export function InvitationAffiliateLink() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const partnerInviteCode = searchParams.get('partner_invite_code') || undefined;

  const { product, isPending } = useFetchInvite(productId);
  const { applyInviteAffiliation } = useApplyInviteAffiliation();

  const { t } = useTranslation("invitationAffiliateLink");
  const navigate = useNavigate();

  const { isAuthenticated } = useAuth();

  const handleAffiliateClick = async () => {
    if (isAuthenticated) {
      await applyInviteAffiliation({ productId, partner_invite_code: partnerInviteCode });
    } else {
      const redirectPath = partnerInviteCode
        ? `/invitation-affiliate/${productId}?partner_invite_code=${partnerInviteCode}`
        : `/invitation-affiliate/${productId}`;
      localStorage.setItem(
        "redirectAfterEmailVerification",
        redirectPath
      );
      navigate("/login", {
        state: { from: redirectPath },
      });
    }
  };

  useEffect(() => {
    const redirectUrl = localStorage.getItem("redirectAfterEmailVerification");

    const handleBeforeUnload = () => {
      if (redirectUrl) {
        localStorage.removeItem("redirectAfterEmailVerification");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (
        redirectUrl &&
        location.pathname !== `/invitation-affiliate/${productId}`
      ) {
        localStorage.removeItem("redirectAfterEmailVerification");
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [location.pathname, productId]);

  const commissionText = (() => {
    if (!product) return '';
    if (product.commission_rule_type === 'percentage') {
      return `${product.commission_value}%`;
    }
    // cpa (valor fixo)
    return product.commission_value_formatted ?? String(product.commission_value ?? '');
  })();

  return (
    <LayoutOutside classNameMainContainer="mt-[10%]" hideButton>
      {isPending ? (
        <Loader fullScreen />
      ) : (
        <>
          {product ? (
            <section className="max-w-md w-full flex flex-col items-center space-y-8">
              <Avatar className="h-20 w-20 bg-blackA1">
                <AvatarImage
                  src={product?.product.images[0]?.url}
                  className="w-full h-full rounded-full"
                  alt={`${product.product.name} image`}
                  loading="lazy"
                />
                <AvatarFallback className="bg-elevated text-xl font-medium text-muted">
                  {product.product.name[0]}
                </AvatarFallback>
              </Avatar>

              <div className="space-y-1 text-center">
                <h4 className="font-medium">{t("invite_affiliation")}</h4>
                <h1 className="text-3xl font-semibold">
                  {product.product.name}
                </h1>
              </div>

              <div className="border w-full p-4 space-y-6 rounded">
                <Table className="w-full">
                  <TableBody>
                    <TableRow className="border-transparent ">
                      <TableCell className="text-gray-400">
                        {t("producer")}:
                      </TableCell>
                      <TableCell className="text-white">
                        {product.product.producer_name}
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-transparent ">
                      <TableCell className="text-gray-400">
                        {t("commission")}:
                      </TableCell>
                      <TableCell className="text-white">
                        {commissionText}
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-transparent ">
                      <TableCell className="text-gray-400">
                        {t("attribution")}:
                      </TableCell>
                      <TableCell className="text-white">
                        {product.attribution === "first_click"
                          ? t("first_click")
                          : t("last_click")}
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-transparent ">
                      <TableCell className="text-gray-400">
                        {t("cookie_duration")}:
                      </TableCell>
                      <TableCell className="text-white">
                        {product.cookie_duration} {t("days")}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Button className="w-full" onClick={handleAffiliateClick}>
                  {t("affiliate_now")}
                </Button>
              </div>
            </section>
          ) : (
            <div>
              <h2>{t("no_invite")}</h2>
            </div>
          )}
        </>
      )}
    </LayoutOutside>
  );
}
